import React, { useState, useEffect, useRef } from 'react';

function CoursePicker({ golfCourses, selectedCourseIds, onSelectionChange }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const dropdownRef = useRef(null);

  // Filter courses based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredCourses([]);
      return;
    }

    const searchLower = searchTerm.toLowerCase();
    const filtered = golfCourses
      .filter(course => {
        const nameMatch = course.name?.toLowerCase().includes(searchLower);
        const clubMatch = course.club_name?.toLowerCase().includes(searchLower);
        const cityMatch = course.city?.toLowerCase().includes(searchLower);
        const stateMatch = course.state?.toLowerCase().includes(searchLower);
        return nameMatch || clubMatch || cityMatch || stateMatch;
      })
      .slice(0, 20); // Limit results

    setFilteredCourses(filtered);
  }, [searchTerm, golfCourses]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectCourse = (course) => {
    if (!selectedCourseIds.includes(course.course_id)) {
      onSelectionChange([...selectedCourseIds, course.course_id]);
    }
    setSearchTerm('');
    setShowDropdown(false);
  };

  const handleRemoveCourse = (courseId) => {
    onSelectionChange(selectedCourseIds.filter(id => id !== courseId));
  };

  // Get course details for selected IDs
  const selectedCourses = selectedCourseIds
    .map(id => golfCourses.find(c => c.course_id === id))
    .filter(Boolean);

  return (
    <div style={{ width: '100%' }}>
      {/* Search Input */}
      <div ref={dropdownRef} style={{ position: 'relative', marginBottom: 12 }}>
        <input
          type="text"
          value={searchTerm}
          onChange={e => {
            setSearchTerm(e.target.value);
            setShowDropdown(true);
          }}
          onFocus={() => setShowDropdown(true)}
          placeholder="Search courses by name, city, or state..."
          style={{
            width: '100%',
            padding: '10px 12px',
            borderRadius: 8,
            border: '1px solid #ddd',
            fontSize: 14,
            boxSizing: 'border-box'
          }}
        />

        {/* Dropdown Results */}
        {showDropdown && filteredCourses.length > 0 && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            background: '#fff',
            border: '1px solid #ddd',
            borderRadius: 8,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            maxHeight: 300,
            overflowY: 'auto',
            zIndex: 1000
          }}>
            {filteredCourses.map(course => {
              const isSelected = selectedCourseIds.includes(course.course_id);
              return (
                <div
                  key={course.course_id}
                  onClick={() => !isSelected && handleSelectCourse(course)}
                  style={{
                    padding: '10px 12px',
                    cursor: isSelected ? 'default' : 'pointer',
                    background: isSelected ? '#f0f0f0' : '#fff',
                    borderBottom: '1px solid #eee',
                    opacity: isSelected ? 0.6 : 1
                  }}
                  onMouseEnter={e => {
                    if (!isSelected) e.target.style.background = '#f5f5f5';
                  }}
                  onMouseLeave={e => {
                    if (!isSelected) e.target.style.background = '#fff';
                  }}
                >
                  <div style={{ fontWeight: 500, color: '#333', fontSize: 14 }}>
                    {course.name}
                    {isSelected && <span style={{ marginLeft: 8, color: '#1D665D', fontSize: 12 }}>(Selected)</span>}
                  </div>
                  <div style={{ fontSize: 12, color: '#666' }}>
                    {course.city}{course.city && course.state ? ', ' : ''}{course.state}
                    {course.club_name && course.club_name !== course.name && (
                      <span> - {course.club_name}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {showDropdown && searchTerm && filteredCourses.length === 0 && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            background: '#fff',
            border: '1px solid #ddd',
            borderRadius: 8,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            padding: '12px',
            color: '#666',
            fontSize: 14,
            zIndex: 1000
          }}>
            No courses found matching "{searchTerm}"
          </div>
        )}
      </div>

      {/* Selected Courses Chips */}
      {selectedCourses.length > 0 && (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
          marginTop: 8
        }}>
          {selectedCourses.map(course => (
            <div
              key={course.course_id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                background: '#e4f8ec',
                border: '1px solid #1D665D',
                borderRadius: 20,
                padding: '4px 10px',
                fontSize: 13
              }}
            >
              <span style={{ color: '#1D665D', fontWeight: 500 }}>
                {course.name}
              </span>
              <span style={{ color: '#666', fontSize: 11 }}>
                ({course.city}, {course.state})
              </span>
              <button
                onClick={() => handleRemoveCourse(course.course_id)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#1D665D',
                  fontSize: 16,
                  fontWeight: 'bold',
                  padding: '0 2px',
                  lineHeight: 1
                }}
              >
                x
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Count display */}
      <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
        {selectedCourseIds.length} course{selectedCourseIds.length !== 1 ? 's' : ''} selected
      </div>
    </div>
  );
}

export default CoursePicker;
